/**
 * The line, off the main thread.
 *
 * A chiller-trip comparison is three policies plus a control, each a
 * two-month run of a ten-bay fab - a few seconds of solver time, which on
 * the thread that draws the floor would be a few seconds of frozen floor.
 * The seed studies (an excursion at four sampling rates over six seeds, a
 * maintenance decision over eight) are longer still. Same shape as
 * solvers.worker.ts: one request kind per job, an id to correlate the reply,
 * errors carried back as data. The scenario module is imported unchanged;
 * nothing in it knows it is on a worker.
 */

import {
  PM_POLICIES,
  POLICIES,
  QTIME_POLICIES,
  operatingCurve,
  runChillerTrip,
  runExcursionStudy,
  runPmStudy,
  runQtime,
  type ChillerTripResult,
  type ExcursionStudy,
  type OperatingPoint,
  type PmPolicy,
  type PmStudy,
  type Policy,
  type QtimePolicy,
  type ProductMix,
  type QtimeResult,
} from './line/scenario'

export interface TripRequest {
  id: number
  kind: 'trip'
  load: number
  restoreH: number
  seed: number
  /** Which products the line carries - scenario.ts, ProductMix. */
  mix: ProductMix
}

export interface CurveRequest {
  id: number
  kind: 'curve'
  loads: number[]
  seed: number
}

export interface QtimeRequest {
  id: number
  kind: 'qtime'
  load: number
  windowH: number
  seed: number
}

export interface ExcursionRequest {
  id: number
  kind: 'excursion'
  rates: number[]
  seeds: number[]
  load: number
}

export interface PmRequest {
  id: number
  kind: 'pm'
  seeds: number[]
  load: number
}

export type LineRequest = TripRequest | CurveRequest | QtimeRequest | ExcursionRequest | PmRequest

export interface TripReply {
  id: number
  kind: 'trip'
  results: Record<Policy, ChillerTripResult>
}

export interface CurveReply {
  id: number
  kind: 'curve'
  points: OperatingPoint[]
}

export interface QtimeReply {
  id: number
  kind: 'qtime'
  results: Record<QtimePolicy, QtimeResult>
}

export interface ExcursionReply {
  id: number
  kind: 'excursion'
  studies: ExcursionStudy[]
}

export interface PmReply {
  id: number
  kind: 'pm'
  studies: Record<PmPolicy, PmStudy>
}

export interface LineError {
  id: number
  kind: 'error'
  message: string
}

export type LineReply = TripReply | CurveReply | QtimeReply | ExcursionReply | PmReply | LineError

export function runTrip(req: TripRequest): Record<Policy, ChillerTripResult> {
  const out = {} as Record<Policy, ChillerTripResult>
  for (const policy of POLICIES) out[policy] = runChillerTrip({ policy, load: req.load, restoreH: req.restoreH, seed: req.seed, mix: req.mix })
  return out
}

export function runCurve(req: CurveRequest): OperatingPoint[] {
  return operatingCurve(req.loads, { seed: req.seed })
}

export function runQtimeBoth(req: QtimeRequest): Record<QtimePolicy, QtimeResult> {
  const out = {} as Record<QtimePolicy, QtimeResult>
  for (const policy of QTIME_POLICIES) out[policy] = runQtime({ policy, load: req.load, windowH: req.windowH, seed: req.seed })
  return out
}

export function runExcursionRates(req: ExcursionRequest): ExcursionStudy[] {
  return req.rates.map((rate) => runExcursionStudy(rate, req.seeds, { load: req.load }))
}

export function runPmBoth(req: PmRequest): Record<PmPolicy, PmStudy> {
  const out = {} as Record<PmPolicy, PmStudy>
  for (const policy of PM_POLICIES) out[policy] = runPmStudy(policy, req.seeds, { load: req.load })
  return out
}

export function answer(req: LineRequest): LineReply {
  switch (req.kind) {
    case 'trip':
      return { id: req.id, kind: 'trip', results: runTrip(req) }
    case 'curve':
      return { id: req.id, kind: 'curve', points: runCurve(req) }
    case 'qtime':
      return { id: req.id, kind: 'qtime', results: runQtimeBoth(req) }
    case 'excursion':
      return { id: req.id, kind: 'excursion', studies: runExcursionRates(req) }
    case 'pm':
      return { id: req.id, kind: 'pm', studies: runPmBoth(req) }
  }
}

function post(msg: LineReply): void {
  ;(self as unknown as Worker).postMessage(msg)
}

self.onmessage = (e: MessageEvent<LineRequest>) => {
  const req = e.data
  try {
    post(answer(req))
  } catch (err) {
    post({ id: req.id, kind: 'error', message: err instanceof Error ? err.message : String(err) })
  }
}
